import { isError, normalizeValue, type NormalizedValue, type SerializedError, truncate } from '#@/normalizeValue.ts'

export type LogFields = Record<string, unknown>

export type LogRecord = LogFields & {
  message: string
  logger: string
  error?: SerializedError
  /** Primitives from `context`, extra positional `Error` values, and a caller `error` displaced by a positional one. */
  args?: (SerializedError | NormalizedValue)[]
  serializationFailed?: true
  oversized?: true
}

export type BuildRecordArgs = {
  loggerName: string
  contextFields: LogFields | undefined
  boundFields: LogFields
  callArgs: readonly unknown[]
}

/**
 * Turns one log call into the plain object the structured format emits. Precedence from lowest to highest: context
 * fields, bound fields, call-site fields, reserved keys (R9, R14). Every value is JSON-compatible on return (R12), no
 * string exceeds the string cap (R13), and the JSON form stays under {@link MAX_RECORD_BYTES} (R13). A failure anywhere
 * in that work yields `{ message, logger, serializationFailed: true }` instead of a throw (R11).
 */
export function buildRecord({ loggerName, contextFields, boundFields, callArgs }: BuildRecordArgs): LogRecord {
  try {
    const call = splitCallArgs(callArgs)
    const merged = mergeFields({ contextFields, boundFields, call })
    return capRecordSize(normalizeRecord({ merged, message: call.message, loggerName }))
  } catch {
    return { message: fallbackMessage(callArgs), logger: truncate(loggerName), serializationFailed: true }
  }
}

/** Largest JSON form kept, in bytes (R13). */
export const MAX_RECORD_BYTES = 48 * 1024

type SplitCall = {
  /**
   * The first argument when it is a string, or `error.message` when the first `Error` arrives before any string. A
   * reserved value that overwrites a caller `message` field (R14); `undefined` keeps a caller field.
   */
  message: string | undefined
  positionalError: Error | undefined
  fields: LogFields
  args: unknown[]
}

function splitCallArgs(callArgs: readonly unknown[]): SplitCall {
  const call: SplitCall = { message: undefined, positionalError: undefined, fields: {}, args: [] }
  const fieldEntries: [string, unknown][] = []
  for (const [index, value] of callArgs.entries()) {
    if (index === 0 && typeof value === 'string') {
      call.message = value
    } else if (isError(value)) {
      if (call.positionalError === undefined) {
        call.positionalError = value
        call.message ??= value.message
      } else {
        call.args.push(value)
      }
    } else if (isPlainObject(value)) {
      fieldEntries.push(...Object.entries(value))
    } else {
      call.args.push(value)
    }
  }
  // `Object.fromEntries` defines data properties, so a caller key named `__proto__` stays a key; a later object wins.
  call.fields = Object.fromEntries(fieldEntries)
  return call
}

function mergeFields({
  contextFields,
  boundFields,
  call,
}: {
  contextFields: LogFields | undefined
  boundFields: LogFields
  call: SplitCall
}) {
  const merged = { ...contextFields, ...boundFields, ...call.fields }
  // The selected console method provides the level (R2).
  delete merged.level
  const callerError = merged.error
  if (call.positionalError !== undefined) {
    merged.error = call.positionalError
    if (isError(callerError)) {
      call.args.push(callerError)
    }
  }
  if (call.args.length > 0) {
    merged.args = call.args
  }
  return merged
}

function normalizeRecord({
  merged,
  message,
  loggerName,
}: {
  merged: LogFields
  message: string | undefined
  loggerName: string
}): LogRecord {
  const ancestors = new Set<object>([merged])
  const entries: [string, NormalizedValue | SerializedError][] = []
  for (const [key, value] of Object.entries(merged)) {
    const normalized = normalizeValue(value, { value: 1, cause: 0 }, ancestors)
    if (normalized !== undefined) {
      entries.push([key, normalized])
    }
  }
  // `Object.fromEntries` defines data properties, so a key named `__proto__` stays a key (R12).
  const fields = Object.fromEntries(entries)
  const callerMessage = fields.message
  return {
    ...fields,
    message: message === undefined ? (typeof callerMessage === 'string' ? callerMessage : '') : truncate(message),
    logger: truncate(loggerName),
  }
}

/**
 * Replaces a record over {@link MAX_RECORD_BYTES} with `{ message, logger, error?, oversized: true }`. The replacement
 * is measured again: `error` is dropped when it alone exceeds the budget, and `message` is halved until the rest fits.
 * `message` is the only field the loop shortens, so the budget holds unless the logger name itself exceeds the budget.
 * A name is a code literal from the `logger(...)` call, not caller data.
 */
function capRecordSize(record: LogRecord): LogRecord {
  if (isWithinBudget(record)) {
    return record
  }
  const { message, logger, error } = record
  if (error !== undefined) {
    const withError: LogRecord = { message, logger, error, oversized: true }
    if (isWithinBudget(withError)) {
      return withError
    }
  }
  let replacement: LogRecord = { message, logger, oversized: true }
  while (!isWithinBudget(replacement) && replacement.message !== '') {
    const kept = replacement.message.slice(0, Math.floor(replacement.message.length / 2))
    replacement = { message: kept === '' ? '' : `${kept}…[truncated]`, logger, oversized: true }
  }
  return replacement
}

function isWithinBudget(record: LogRecord): boolean {
  const json = JSON.stringify(record)
  // One UTF-16 code unit encodes to at most three UTF-8 bytes, so a short JSON string needs no byte count.
  return (
    json.length * 3 <= MAX_RECORD_BYTES
    || (json.length <= MAX_RECORD_BYTES && new TextEncoder().encode(json).byteLength <= MAX_RECORD_BYTES)
  )
}

function fallbackMessage(callArgs: readonly unknown[]): string {
  const [first] = callArgs
  return typeof first === 'string' ? truncate(first) : ''
}

/** An object whose own keys merge into the record: a literal or a null-prototype object, not a class instance. */
function isPlainObject(value: unknown): value is LogFields {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const prototype: unknown = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}
